import React, { useState, KeyboardEvent } from 'react';
import { Box, Chip, InputAdornment } from '@mui/material';
import { StyledTextField } from 'components/Inputs';
import { validateISO6346 } from '../transportCloudValidators';

interface ContainerNumbersInputProps {
  value: string[];
  onChange: (containerNumbers: string[]) => void;
  onBlur?: () => void;
  error?: boolean;
  errorMessage?: string;
  disabled?: boolean;
  maxCount?: number;
  placeholder?: string;
  width?: number;
}

export const ContainerNumbersInput: React.FC<ContainerNumbersInputProps> = ({
  value = [],
  onChange,
  onBlur,
  error = false,
  errorMessage,
  disabled = false,
  maxCount,
  placeholder,
  width = 280,
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      addContainerNumber();
    }
  };

  const addContainerNumber = () => {
    const trimmedValue = inputValue.trim().toUpperCase();

    if (!trimmedValue) return;

    if (maxCount === 0) return;

    if (value.includes(trimmedValue)) {
      return;
    }

    onChange([...value, trimmedValue]);
    setInputValue('');
  };

  const handleBlur = () => {
    addContainerNumber();
    onBlur?.();
  };

  const removeContainerNumber = (indexToRemove: number) => {
    const newValue = value.filter((_, index) => index !== indexToRemove);
    onChange(newValue);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const getChipColor = (containerNumber: string, index: number) => {
    if (maxCount && maxCount > 0 && index >= maxCount) {
      return 'error';
    }

    if (!validateISO6346(containerNumber)) {
      return 'error';
    }

    return 'primary';
  };

  const getChipVariant = (containerNumber: string, index: number) => {
    if (maxCount && maxCount > 0 && index >= maxCount) {
      return 'filled';
    }

    if (!validateISO6346(containerNumber)) {
      return 'filled';
    }

    return 'outlined';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1 }}>
        <StyledTextField
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onBlur={handleBlur}
          error={error}
          disabled={disabled || maxCount === 0}
          placeholder={placeholder}
          size='small'
          variant='outlined'
          padding='dense'
          width={width}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <span
                  style={{
                    color:
                      maxCount !== undefined && value.length > maxCount
                        ? '#cf2b2b'
                        : 'inherit',
                    fontWeight: 'normal',
                  }}
                >
                  {value.length}/{maxCount}
                </span>
              </InputAdornment>
            ),
          }}
        />
        {value.length > 0 && (
          <Box
            sx={{
              mt: 0.5,
              mb: 0.75,
              display: 'flex',
              flexWrap: 'wrap',
              gap: 0.5,
            }}
          >
            {value.map((containerNumber, index) => (
              <Chip
                key={index}
                label={containerNumber}
                onDelete={() => removeContainerNumber(index)}
                size='small'
                color={getChipColor(containerNumber, index)}
                variant={getChipVariant(containerNumber, index)}
                disabled={disabled}
                sx={{
                  ...(getChipColor(containerNumber, index) === 'error' && {
                    backgroundColor: '#cf2b2b',
                    color: 'white',
                    '& .MuiChip-deleteIcon': {
                      color: 'white',
                    },
                  }),
                }}
              />
            ))}
          </Box>
        )}
      </Box>
      <Box sx={{ paddingTop: '6px', paddingLeft: '5px', minWidth: '400px' }}>
        {error && errorMessage && (
          <span style={{ color: '#cf2b2b', fontSize: '0.75rem' }}>
            {errorMessage}
          </span>
        )}
      </Box>
    </Box>
  );
};
